import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useDashboard } from "../Hooks/useDashboard";
import { formatAmountINR } from "../Constants";
import { getCategoryColor } from ".";
import { Grid } from "lucide-react";

const generateGroups = (apiData) => {
  if (!apiData || apiData.length === 0) return [];

  const typeMap = {};

  // Iterate over first item to get all keys
  const sample = apiData[0];
  Object.entries(sample).forEach(([key, value]) => {
    if (typeof value === "object" && value.amount !== undefined && value.type) {
      const type = value.type; // PURCHASE or REIMBURSEMENT
      if (!typeMap[type]) {
        typeMap[type] = [];
      }
      typeMap[type].push(key);
    }
  });

  // Convert to groups array
  return Object.entries(typeMap).map(([type, keys]) => ({
    label: type === "PURCHASE" ? "Purchase" : "Reimbursement",
    stackId: type.toLowerCase(),
    keys,
  }));
};

const GroupedTooltip = ({ active, payload, label, groups }) => {
  if (active && payload && payload.length) {
    const groupedData = {};
    groups.forEach((group) => {
      groupedData[group.label] = payload
        .filter((entry) => group.keys.includes(entry.dataKey))
        .map((entry) => ({
          name: entry.name,
          value: entry.value,
          color: entry.color,
          type: entry.payload[`${entry.dataKey}_type`], // <- use type from flattened data
        }));
    });

    return (
      <div className="bg-white shadow-md rounded-lg border border-gray-200">
        <p className="font-semibold p-4">{label}</p>
        <hr />
        {Object.entries(groupedData).map(([groupLabel, items]) =>
          items.length > 0 ? (
            <div key={groupLabel} className="mb-2 px-4">
              <p className="font-medium">{groupLabel}:</p>
              <ul className="pl-4 list-disc space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="capitalize">{item.name}</span>:
                    <span className="font-medium">
                      {formatAmountINR(item.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null
        )}
      </div>
    );
  }
  return null;
};
const flattenData = (apiData) => {
  return apiData.map((project) => {
    const flattened = { project: project.project };

    Object.entries(project).forEach(([key, value]) => {
      if (
        typeof value === "object" &&
        value.amount !== undefined &&
        value.type
      ) {
        flattened[key] = Number(value.amount) || 0;
        flattened[`${key}_type`] = value.type;
      }
    });

    return flattened;
  });
};
const GroupedStackedBarChart = ({ filter }) => {
  const [data, setData] = useState([]);
  const [groups, setGroups] = useState([]);
  const { fetchBillSummary, loading } = useDashboard();

  const fetchBillData = async () => {
    const res = await fetchBillSummary({ project: filter });
    const flattened = flattenData(res); // flatten amount/type as discussed earlier
    setData(flattened);
    setGroups(generateGroups(res)); // dynamically generate groups
  };

  useEffect(() => {
    fetchBillData();
  }, [filter]);

  if (loading) {
    return (
      <div className="flex justify-between gap-4 min-h-[360px]">
        {new Array(3).fill(0).map((_, idx) => (
          <div
            key={idx}
            className="w-[33%] bg-gray-200 rounded-xl shadow animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-gray-400 text-center py-20">
        No billing data available
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: 400 }}>
      <h2 className="text-xl font-semibold mb-4">Billing Summary</h2>
      <div className="w-full overflow-x-auto custom-scroll">
        <div
          style={{
            width: `max(${data.length * 170}px, 750px, 47vw)`,
            height: 330,
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={data}
              margin={{ top: 20, right: 0, left: 5, bottom: 0 }}
            >
              <XAxis
                dataKey="project"
                angle={0} // no angle needed for wrapping
                textAnchor="middle"
                interval={0}
                height={80}
                tick={({ x, y, payload }) => {
                  const maxWidth = 80; // maximum width for each line
                  const words = payload.value.split(" ");
                  const lines = [];
                  let currentLine = "";

                  words.forEach((word) => {
                    const testLine = currentLine
                      ? currentLine + " " + word
                      : word;
                    // Rough estimate of text width (12px font size)
                    if (testLine.length * 6 < maxWidth) {
                      currentLine = testLine;
                    } else {
                      lines.push(currentLine);
                      currentLine = word;
                    }
                  });
                  if (currentLine) lines.push(currentLine);

                  return (
                    <g transform={`translate(${x},${y + 10})`}>
                      {lines.map((line, index) => (
                        <text
                          key={index}
                          x={0}
                          y={index * 14} // line height
                          textAnchor="middle"
                          fill="#374151"
                          fontSize={12}
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
              />
              <YAxis tickFormatter={(value) => formatAmountINR(value)} />
              <Tooltip
                content={<GroupedTooltip groups={groups} />}
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />
              {groups.map((group) =>
                group.keys.map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId={group.stackId}
                    fill={getCategoryColor(key)}
                    name={key.replaceAll("_", " ")}
                    isAnimationActive={false}
                  />
                ))
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default GroupedStackedBarChart;
